import React, { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, ButtonVariant } from '@/shared/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { useToast } from '@/shared/contexts/ToastContext';
import { createUsersApi } from '@/features/system/api/usersApi';
import { useRuntimeConfigContext } from '@/shared/providers/RuntimeConfigProvider';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  onChangePassword: (newPassword: string) => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  username,
  onChangePassword,
}) => {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { getConfigValue } = useRuntimeConfigContext();
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rails parity: component.passwordPolicy.complexityHint renders as a muted
  // help caption under the password field when the site has configured it.
  const passwordHint =
    (getConfigValue('component.passwordPolicy.complexityHint') as string | undefined)?.trim() || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword.trim()) return;

    setIsSubmitting(true);
    try {
      const usersApi = createUsersApi(apiClient);
      await usersApi.checkPasswordPolicy(newPassword);
    } catch (error) {
      const responseData = (error as any)?.response?.data;
      const errors: { error_specifier?: string; error_class?: string; message?: string }[] =
        responseData?.errors ?? [];
      const isPolicyViolation = errors.some(
        (e) =>
          e.error_specifier === 'illegal_value' &&
          e.error_class === 'validation_error'
      );
      const errorMessage = isPolicyViolation
        ? t('system.userManagement.users.passwordPolicyViolation')
        : responseData?.message ||
          (error as Error)?.message ||
          t('system.userManagement.users.passwordPolicyError') ||
          'Password does not meet policy requirements';
      showToast(errorMessage, 'error');
      setIsSubmitting(false);
      return;
    }

    try {
      await onChangePassword(newPassword);
      setNewPassword('');
      onClose();
    } catch (error) {
      throw new Error("Error changing password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
        onKeyDown={(e) => e.key === 'Escape' && handleClose()}
        role="button"
        tabIndex={-1}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative bg-surface rounded-lg shadow-lg w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text">
            {t('system.userManagement.users.changePassword') || 'Change password'}
          </h2>
          <button
            onClick={handleClose}
            className="text-text hover:text-text-muted transition-colors"
            data-testid="close-change-password-modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Username */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text mb-2">
              {t('system.userManagement.users.columns.username') || 'Username'}
            </label>
            <Input
              type="text"
              value={username}
              disabled
              className="bg-surface-alt"
              data-testid="username-input"
            />
          </div>

          {/* New Password */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text mb-2">
              {t('system.userManagement.users.newPassword') || 'New password'} <span className="text-error">*</span>
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('system.userManagement.users.enterNewPassword') || 'Enter new password'}
                required
                className="pr-10"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordHint && (
              <p className="text-text-muted text-xs mt-1">{passwordHint}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant={ButtonVariant.Outline}
              size={ButtonSize.Sm}
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="cancel-change-password-button"
            >
              {t('global.cancel') || 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant={ButtonVariant.Primary}
              size={ButtonSize.Sm}
              disabled={!newPassword.trim() || isSubmitting}
              data-testid="submit-change-password-button"
            >
              {isSubmitting
                ? (t('common.saving') || 'Saving...')
                : (t('system.userManagement.users.changePassword') || 'Change password')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;